import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

interface DateRange {
  start: Date;
  end: Date;
}

type PresetKey = '7d' | '30d' | '90d' | 'quarter' | 'year' | 'custom';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  presets?: PresetKey[];
}

const PRESET_LABELS: Record<PresetKey, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'quarter': 'This quarter',
  'year': 'This year',
  'custom': 'Custom',
};

function getPresetRange(preset: PresetKey): DateRange {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  switch (preset) {
    case '7d':
      return {
        start: new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000),
        end,
      };
    case '30d':
      return {
        start: new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000),
        end,
      };
    case '90d':
      return {
        start: new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000),
        end,
      };
    case 'quarter': {
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      return { start: quarterStart, end };
    }
    case 'year': {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      return { start: yearStart, end };
    }
    default:
      return { start: new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000), end };
  }
}

function detectPreset(range: DateRange): PresetKey | null {
  const now = new Date();
  const daysDiff = Math.round((range.end.getTime() - range.start.getTime()) / (24 * 60 * 60 * 1000));
  const isEndToday = range.end.toDateString() === now.toDateString();

  if (isEndToday) {
    if (daysDiff === 7) return '7d';
    if (daysDiff === 30) return '30d';
    if (daysDiff === 90) return '90d';
  }

  // Check quarter
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  if (range.start.toDateString() === quarterStart.toDateString() && isEndToday) {
    return 'quarter';
  }

  // Check year
  const yearStart = new Date(now.getFullYear(), 0, 1);
  if (range.start.toDateString() === yearStart.toDateString() && isEndToday) {
    return 'year';
  }

  return 'custom';
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatInputDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function DateRangePicker({
  value,
  onChange,
  presets = ['7d', '30d', '90d', 'quarter', 'year'],
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(formatInputDate(value.start));
  const [customEnd, setCustomEnd] = useState(formatInputDate(value.end));
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentPreset = detectPreset(value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (preset: PresetKey) => {
    if (preset === 'custom') {
      setShowCustom(true);
      setCustomStart(formatInputDate(value.start));
      setCustomEnd(formatInputDate(value.end));
    } else {
      onChange(getPresetRange(preset));
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  const handleCustomApply = () => {
    const start = new Date(customStart);
    const end = new Date(customEnd);
    end.setHours(23, 59, 59);

    if (start <= end) {
      onChange({ start, end });
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="text-gray-700">
          {currentPreset && currentPreset !== 'custom'
            ? PRESET_LABELS[currentPreset]
            : `${formatDate(value.start)} - ${formatDate(value.end)}`}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
          {!showCustom ? (
            <div className="p-2">
              {presets.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetClick(preset)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                    currentPreset === preset
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {PRESET_LABELS[preset]}
                </button>
              ))}
              <div className="border-t border-gray-200 mt-2 pt-2">
                <button
                  onClick={() => handlePresetClick('custom')}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                    currentPreset === 'custom'
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  Custom range
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="start-date" className="block text-xs font-medium text-gray-500 mb-1">Start date</label>
                <input
                  id="start-date"
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label htmlFor="end-date" className="block text-xs font-medium text-gray-500 mb-1">End date</label>
                <input
                  id="end-date"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCustom(false)}
                  className="flex-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Back
                </button>
                <button
                  onClick={handleCustomApply}
                  className="flex-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
